import { RecordResponse } from '@/models/ServiceTypes';
import { buildRoleAwarePrefix } from '@/utils/api';
import { getCachedEntriesForEndpoint } from '@/utils/rtkQueryCache';
import { createApi } from '@reduxjs/toolkit/query/react';
import { waServicesQuery } from './baseQueries';
import { WARootState } from './store';

// --- Types ---
export interface RiskRecord {
  RiskId: number;
  Name: string;
  Description?: string | null;
  Code?: string | null;
  NumberAtRisk?: number | null;
  NumberPossible?: number | null;
  RiskPercentage?: number | null;
}

export type RisksResponse = RecordResponse<RiskRecord>;

export interface OpportunityRuleRecord {
  OppRuleId: number;
  Name: string;
  RuleType: number;
  RuleTypeDisplay: string;
  NewNumber: number;
  NewAmount: number;
  ExistingNumber: number;
  ExistingAmount: number;
  ConversionNumber: number;
  ConversionAmount: number;
  MinAmount: number;
  MaxAmount: number;
}

export interface OpportunityTypeRecord {
  OpportunityTypeId: number;
  Name: string;
  Rules: OpportunityRuleRecord[];
}

export type OpportunitiesResponse = RecordResponse<OpportunityTypeRecord>;

export const insightsApi = createApi({
  reducerPath: 'insightsApi',
  baseQuery: waServicesQuery,
  endpoints: (builder) => ({
    // DEMO-ONLY ENDPOINT
    // This per-investor opportunity types request is a temporary stopgap
    // to power the Clients grid "Insights" column while the proper
    // production insights endpoints are designed and implemented.
    // Notes:
    // - It fetches data for a single investorId.
    // - RTK Query memoizes responses by investorId to avoid duplicate
    //   network calls within the same session/cache window.
    // - Replace with a batched/bulk advisor-level endpoint when available.
    getInvestorOpportunityTypesForDemo: builder.query<
      OpportunitiesResponse,
      number
    >({
      queryFn: async (investorId, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/investors/${String(investorId)}/opportunityTypes`,
          method: 'GET',
        });
        if (result.error) return { error: result.error };
        return { data: result.data as OpportunitiesResponse };
      },
    }),
    getInvestorRisks: builder.query<RisksResponse, number | undefined>({
      queryFn: async (investorId, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const prefix = buildRoleAwarePrefix(currentRole, investorId);
        const result = await baseQuery({
          url: `${prefix}/risks`,
          method: 'GET',
          params: { _: Date.now().toString() },
        });
        if (result.error) return { error: result.error };
        return { data: result.data as RisksResponse };
      },
    }),
    getInvestorOpportunities: builder.query<OpportunitiesResponse, number>({
      queryFn: async (investorId, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const prefix = buildRoleAwarePrefix(currentRole, investorId);
        const result = await baseQuery({
          url: `${prefix}/opportunityTypes`,
          method: 'GET',
          params: { _: Date.now().toString() },
        });
        if (result.error) return { error: result.error };
        return { data: result.data as OpportunitiesResponse };
      },
    }),
    // Advisor-level: investors at risk by risk type
    getRiskInvestors: builder.query<
      RecordResponse<AdvisorRiskInvestor>,
      { riskType: RiskTypeEnum; atRisk?: boolean }
    >({
      query: ({ riskType, atRisk = true }) => ({
        url: `/api/advisor/risks/${String(riskType)}/Investors`,
        method: 'GET',
        params: {
          atRisk,
          'sort[0].Property': 'Name',
          'sort[0].Direction': 'asc',
          'sort[0].Order': 0,
          _: Date.now().toString(),
        },
      }),
    }),
  }),
});

export const {
  useGetInvestorOpportunityTypesForDemoQuery,
  useGetInvestorRisksQuery,
  useGetInvestorOpportunitiesQuery,
  useGetRiskInvestorsQuery,
} = insightsApi;

// --- Additional endpoints for concrete opportunity items ---
export interface OpportunityInvestorRef {
  InvestorId: number;
  InvestorName: string;
  EstateId: number | null;
  UserId: number | null;
  InvestorHHId: number | null;
}

export interface OpportunityItem {
  OpportunityId: number;
  Investors?: OpportunityInvestorRef[] | null;
  Date: string;
  Amount: number;
  TypeName: string; // e.g., "Money In Motion Opportunities" or "Rollover Opportunities"
  RuleName: string; // e.g., "New Accounts"
  IsNew: boolean;
  WealthAccountId: number | null;
  WealthAccountName: string | null;
  Description: string | null;
  WealthAccountTransactionId: number | null;
  ConversionAmount: number | null;
  StatusId: OpportunityStatusEnum;
  StatusName: string; // e.g., "Existing Opportunities"
  OppRuleId?: OpportunityRuleIdEnum;
}

export type OpportunityItemsResponse = RecordResponse<OpportunityItem>;

declare module '@reduxjs/toolkit/query' {
  // Augment types if needed in the future
}

type OpportunityView = 'Open' | 'Converted' | 'Closed';

type OpportunityRuleQueryBase = {
  startDate: string;
  endDate: string;
  excludedStatuses?: string; // CSV of numeric status ids to exclude
  view?: OpportunityView;
  groupId?: number;
  investorId?: number;
};

type OpportunityRuleQuerySingle = OpportunityRuleQueryBase & {
  ruleId: OpportunityRuleIdEnum;
  ruleIds?: never;
};

type OpportunityRuleQueryMultiple = OpportunityRuleQueryBase & {
  ruleIds: OpportunityRuleIdEnum[];
  ruleId?: never;
};

type OpportunityRuleQueryArgs =
  | OpportunityRuleQuerySingle
  | OpportunityRuleQueryMultiple;

export enum OpportunityRuleIdEnum {
  MoneyInMotionNewAccounts = 3,
  MoneyInMotionLargeDeposits = 7,
  Rollover = 5,
  InvestableCash = 12,
}

// Opportunity status identifiers returned by the API
export enum OpportunityStatusEnum {
  New = 0,
  InProgress = 1,
  Contacted = 2,
  Existing = 3,
  Closed = 4,
  Converted = 5,
}

const ALL_OPPORTUNITY_STATUSES: readonly OpportunityStatusEnum[] = [
  OpportunityStatusEnum.New,
  OpportunityStatusEnum.InProgress,
  OpportunityStatusEnum.Contacted,
  OpportunityStatusEnum.Existing,
  OpportunityStatusEnum.Closed,
  OpportunityStatusEnum.Converted,
];

export const API_DERIVED_OPPORTUNITY_STATUSES: readonly OpportunityStatusEnum[] =
  [OpportunityStatusEnum.New, OpportunityStatusEnum.Existing];

export const SETTABLE_OPPORTUNITY_STATUSES: readonly OpportunityStatusEnum[] = [
  OpportunityStatusEnum.InProgress,
  OpportunityStatusEnum.Contacted,
  OpportunityStatusEnum.Converted,
  OpportunityStatusEnum.Closed,
];

export const CLEAR_OPPORTUNITY_STATUS = -1;

export const OPPORTUNITY_STATUS_NAMES: Record<OpportunityStatusEnum, string> = {
  [OpportunityStatusEnum.New]: 'New Opportunities',
  [OpportunityStatusEnum.InProgress]: 'In Progress Opportunities',
  [OpportunityStatusEnum.Contacted]: 'Contacted Opportunities',
  [OpportunityStatusEnum.Existing]: 'Existing Opportunities',
  [OpportunityStatusEnum.Closed]: 'Closed Opportunities',
  [OpportunityStatusEnum.Converted]: 'Converted Opportunities',
};

export const excludeStatuses = (...excluded: OpportunityStatusEnum[]): string =>
  excluded.join(',');

export const excludeAllStatusesBut = (
  ...kept: OpportunityStatusEnum[]
): string =>
  ALL_OPPORTUNITY_STATUSES.filter((status) => !kept.includes(status)).join(',');

export const EXCLUDED_STATUSES_OPEN_VIEW = excludeStatuses(
  OpportunityStatusEnum.Closed,
  OpportunityStatusEnum.Converted,
);

// Advisor risk types for investor lists and summary mapping
export enum RiskTypeEnum {
  UnusedAggregations = 4,
  UnauthenticatedClients = 2,
  UnacceptedClientInvitations = 3,
}

export interface AdvisorRiskInvestor {
  AdvisorId: number;
  AdvisorName: string;
  InvestorId: number;
  UserId: number;
  EstateId: number | null;
  InvestorHHId: number | null;
  Name: string;
  UserName: string;
  AtRisk: boolean;
}

export const extendedInsightsApi = insightsApi.injectEndpoints({
  endpoints: (builder) => ({
    getRuleOpportunities: builder.query<
      OpportunityItemsResponse,
      OpportunityRuleQueryArgs
    >({
      async queryFn(args, _queryApi, _extraOptions, baseQuery) {
        const baseRuleIds = 'ruleIds' in args ? args.ruleIds : [args.ruleId];
        const uniqueRuleIds = Array.from(new Set(baseRuleIds));

        if (uniqueRuleIds.length === 0) {
          return {
            data: { Records: [], TotalLength: 0, SummedValue: null },
          };
        }

        const allRecords: OpportunityItem[] = [];
        let totalLength = 0;
        let summedValue = 0;
        let hasSummedValue = false;

        for (const ruleId of uniqueRuleIds) {
          const result = await baseQuery({
            url: `/api/advisor/opportunityRules/${String(ruleId)}/opportunities`,
            method: 'GET',
            params: {
              startDate: args.startDate,
              endDate: args.endDate,
              excludedStatuses:
                args.excludedStatuses ?? EXCLUDED_STATUSES_OPEN_VIEW,
              view: args.view,
              ...(args.groupId ? { GroupId: args.groupId } : {}),
              ...(typeof args.investorId === 'number'
                ? { InvestorId: args.investorId }
                : {}),
              'sort[0].Property': 'Name',
              'sort[0].Direction': 'asc',
              'sort[0].Order': 0,
              'group.ValueProperty': 'StatusId',
              'group.DisplayProperty': 'StatusName',
              'group.SortProperty': 'StatusId',
              // _: Date.now().toString(),
            },
          });

          if (result.error) {
            return { error: result.error };
          }

          const data = result.data as OpportunityItemsResponse;
          const records = data.Records;
          const recordsWithRule = records.map((record) => ({
            ...record,
            OppRuleId: ruleId,
          }));
          allRecords.push(...recordsWithRule);
          totalLength += data.TotalLength;

          if (typeof data.SummedValue === 'number') {
            summedValue += data.SummedValue;
            hasSummedValue = true;
          }
        }

        return {
          data: {
            Records: allRecords,
            TotalLength: totalLength,
            SummedValue: hasSummedValue ? summedValue : null,
          },
        };
      },
    }),
    updateOpportunity: builder.mutation<
      OpportunityItem,
      {
        ruleId: OpportunityRuleIdEnum;
        opportunityId: number;
        body: Partial<OpportunityItem> & {
          InProgress?: boolean;
          IsClosed?: boolean;
          IsConverted?: boolean;
          ConversionAmount?: number | null;
          ContactedDate?: string | null;
          IsNew?: boolean;
          OppRuleId?: number;
          ToDoItemTitle?: string;
        };
      }
    >({
      query: ({ ruleId, opportunityId, body }) => ({
        url: `/api/advisor/opportunityRules/${String(ruleId)}/opportunities/${String(opportunityId)}`,
        method: 'PUT',
        body,
      }),
      async onQueryStarted({ ruleId, opportunityId, body }, api) {
        const { dispatch, queryFulfilled } = api;
        let explicitStatus: OpportunityStatusEnum | null = null;
        if (body.IsClosed) {
          explicitStatus = OpportunityStatusEnum.Closed;
        } else if (body.ContactedDate) {
          explicitStatus = OpportunityStatusEnum.Contacted;
        } else if (body.IsConverted) {
          explicitStatus = OpportunityStatusEnum.Converted;
        } else if (body.InProgress) {
          explicitStatus = OpportunityStatusEnum.InProgress;
        }

        const statusForCachedRecord = (
          rec: OpportunityItem,
        ): OpportunityStatusEnum =>
          explicitStatus ??
          (rec.IsNew
            ? OpportunityStatusEnum.New
            : OpportunityStatusEnum.Existing);

        const patches: Array<{ undo?: () => void }> = [];

        // Iterate all cached getRuleOpportunities queries for this ruleId
        try {
          const state = api.getState();
          for (const entry of getCachedEntriesForEndpoint(
            state,
            extendedInsightsApi,
            'getRuleOpportunities',
          )) {
            const args = entry.originalArgs;

            const matchesRule =
              'ruleId' in args
                ? args.ruleId === ruleId
                : Array.isArray(args.ruleIds) && args.ruleIds.includes(ruleId);
            if (!matchesRule) continue;

            const excluded = (
              args.excludedStatuses ?? EXCLUDED_STATUSES_OPEN_VIEW
            )
              .split(',')
              .map((s) => Number(s.trim()));

            const patch = dispatch(
              extendedInsightsApi.util.updateQueryData(
                'getRuleOpportunities',
                args,
                (draft) => {
                  const records = draft.Records;
                  const idx = records.findIndex(
                    (r) => r.OpportunityId === opportunityId,
                  );
                  if (idx === -1) return;

                  const rec = records[idx];
                  const nextStatus = statusForCachedRecord(rec);

                  // If next status is excluded by this query (e.g., Closed/Converted in Open view), remove it
                  if (excluded.includes(nextStatus)) {
                    records.splice(idx, 1);
                    return;
                  }
                  // Otherwise, update fields in-place
                  rec.StatusId = nextStatus;
                  rec.StatusName = OPPORTUNITY_STATUS_NAMES[nextStatus];
                  if (body.ConversionAmount !== undefined) {
                    rec.ConversionAmount = body.ConversionAmount;
                  }
                },
              ),
            );
            patches.push(patch);
          }
        } catch {
          // best-effort
        }

        try {
          await queryFulfilled;
        } catch {
          // Revert all patches on failure
          for (const p of patches) {
            try {
              p.undo?.();
            } catch {
              console.error('Failed to undo patch');
            }
          }
        }
      },
    }),
  }),
});

export const { useGetRuleOpportunitiesQuery, useUpdateOpportunityMutation } =
  extendedInsightsApi;
